import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Upload, FileText, Loader2, CheckCircle, AlertCircle, Camera } from 'lucide-react';
import { cn } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DocumentAnalyser = () => {
  const [isDragging, setIsDragging] = useState(false);
  const [isAnalysing, setIsAnalysing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      processFile(file);
    }
  };

  const processFile = async (file) => {
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload a JPEG, PNG, WEBP image or PDF file.');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB.');
      return;
    }

    setError(null);
    setAnalysis(null);

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreviewUrl(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setPreviewUrl(null);
    }

    // Convert to base64 and analyse
    const base64 = await fileToBase64(file);
    await analyseDocument(base64);
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const analyseDocument = async (base64Data) => {
    setIsAnalysing(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/ai/document/analyse`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image_base64: base64Data })
      });

      if (!response.ok) {
        throw new Error('Analysis failed');
      }

      const data = await response.json();
      setAnalysis(data.analysis);
    } catch (err) {
      // Log error for debugging (consider replacing with proper error logging service in production)
      if (process.env.NODE_ENV === 'development') {
        console.error('Error analysing document:', err);
      }
      setError('Failed to analyse document. Please try again or call 0208 153 0033 for assistance.');
    }
    setIsAnalysing(false);
  };

  const resetAnalyser = () => {
    setAnalysis(null);
    setError(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <section id="document-analyser" className="py-20 lg:py-28 bg-muted/30">
      <div className="section-container">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="inline-block text-sm font-semibold text-accent uppercase tracking-wider mb-4">
            AI-Powered
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Document Analyser
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Upload a photo of your car finance agreement and our AI will extract 
            the key details and assess your eligibility instantly.
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <Card className="border-border shadow-lg overflow-hidden">
            <CardHeader className="bg-primary text-primary-foreground">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gold/20 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <CardTitle>Finance Agreement Analyser</CardTitle>
                  <CardDescription className="text-primary-foreground/70">
                    Securely extract details from your finance documents
                  </CardDescription>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-6">
              {!analysis ? (
                <>
                  {/* Upload Area */}
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={cn(
                      'border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200',
                      isDragging 
                        ? 'border-accent bg-accent/5' 
                        : 'border-border hover:border-accent/50 hover:bg-muted/50',
                      isAnalysing && 'pointer-events-none opacity-60'
                    )}
                  >
                    {isAnalysing ? (
                      <div className="flex flex-col items-center gap-4">
                        <Loader2 className="w-12 h-12 text-accent animate-spin" />
                        <p className="text-lg font-medium text-foreground">
                          Analysing your document...
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Our AI is extracting the key details
                        </p>
                      </div>
                    ) : previewUrl ? (
                      <div className="space-y-4">
                        <img 
                          src={previewUrl} 
                          alt="Document preview" 
                          className="max-h-48 mx-auto rounded-lg shadow-md"
                        />
                        <p className="text-sm text-muted-foreground">
                          Processing your document...
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                          <Upload className="w-8 h-8 text-accent" />
                        </div>
                        <p className="text-lg font-medium text-foreground mb-2">
                          Drop your finance document here
                        </p>
                        <p className="text-sm text-muted-foreground mb-4">
                          or click to browse files
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                          <Button
                            variant="gold"
                            onClick={() => fileInputRef.current?.click()}
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            Upload Document
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => fileInputRef.current?.click()}
                          >
                            <Camera className="w-4 h-4 mr-2" />
                            Take Photo
                          </Button>
                        </div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp,application/pdf"
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                        <p className="text-xs text-muted-foreground mt-4">
                          Supports JPEG, PNG, WEBP images (max 10MB)
                        </p>
                      </>
                    )}
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="mt-4 p-4 rounded-lg bg-destructive/10 border border-destructive/20 flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-destructive">{error}</p>
                    </div>
                  )}
                </>
              ) : (
                /* Analysis Results */
                <div className="space-y-6">
                  <div className="flex items-center gap-3 text-success">
                    <CheckCircle className="w-6 h-6" />
                    <span className="font-semibold">Analysis Complete</span>
                  </div>

                  {/* Analysis Content */}
                  <div className="prose prose-sm max-w-none">
                    <div className="bg-muted/50 rounded-xl p-6 whitespace-pre-wrap text-sm leading-relaxed">
                      {analysis}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button variant="gold" className="flex-1">
                      Start My Claim
                    </Button>
                    <Button variant="outline" onClick={resetAnalyser}>
                      Analyse Another Document
                    </Button>
                  </div>
                </div>
              )}

              {/* Security Note */}
              <div className="mt-6 p-4 rounded-lg bg-muted/50 border border-border">
                <p className="text-xs text-muted-foreground text-center">
                  🔒 Your documents are processed securely and are not stored. 
                  We are regulated by the Solicitors Regulation Authority (SRA).
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export { DocumentAnalyser };
